import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Calculator,
  CheckCircle2,
  FileText,
  FileUp,
  ListPlus,
  Link2,
  Loader2,
  RefreshCw,
  Scale,
  Send,
  X,
} from 'lucide-react';
import api from '@/lib/api';
import TelaProcessando from '@/components/TelaProcessando';
import { usePode } from '@/stores/authStore';
import PassosDaCotacao from './PassosDaCotacao';
import SeletorDeRodada from './SeletorDeRodada';
import type { Situacao as SituacaoDaCotacao } from './situacao';
import Preloader from '@/components/Preloader';
import LogoFornecedor from '@/components/LogoFornecedor';

type Rodada = {
  id: number;
  numero: number;
  descricao: string;
  data: string | null;
  propostas: { fornecedor: string }[];
};

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  rodadas: Rodada[];
  // Por que a próxima rodada ainda não abre — o mesmo motivo que o servidor daria.
  proxima_rodada: { nome: string; impedimento: string | null };
  situacao: SituacaoDaCotacao;
};

type ItemProposta = { id: number; descricao: string; preco_metro: number | null; fora_da_cotacao?: boolean };

type LinhaSemPreco = { id: number; ordem: number; produto: string; quantidade_m: number };

type SituacaoFornecedor = {
  id: number;
  apelido: string;
  nome: string;
  logo_url: string | null;
  le_pdf: boolean;
  proposta: { id: number; arquivo: string | null; tipo: string; itens_lidos: number; importado_em: string | null } | null;
  pedido_revisao: { em: string; pacote_alvo: number; desconto_pct: number; atendido: boolean } | null;
  linhas_com_preco: number;
  linhas_herdadas: number;
  linhas_sem_preco: LinhaSemPreco[];
  itens_fora_da_cotacao: ItemProposta[];
  itens_da_proposta: ItemProposta[];
};

type Situacao = {
  rodada: Omit<Rodada, 'propostas'>;
  total_linhas: number;
  fornecedores: SituacaoFornecedor[];
};

type Falha = { response?: { status?: number; data?: { errors?: Record<string, string[]>; message?: string } } };

type Conferencia = {
  fornecedor_do_arquivo: { id: number; apelido: string } | null;
  repeticoes: {
    rodada: string;
    fornecedor: string;
    arquivo: string | null;
    nesta_rodada: boolean;
    mesmo_fornecedor: boolean;
  }[];
};

/**
 * O arquivo escolhido para um fornecedor, antes de apurar. Ele é conferido no
 * servidor assim que escolhido; se a conferência avisar de algo, só entra na
 * apuração depois que o comprador confirmar.
 */
type Selecao = {
  arquivo: File;
  conferindo: boolean;
  conferencia: Conferencia | null;
  confirmado: boolean;
};

const temAviso = (selecao: Selecao) =>
  Boolean(selecao.conferencia?.fornecedor_do_arquivo) || (selecao.conferencia?.repeticoes.length ?? 0) > 0;

const prontoParaApurar = (selecao: Selecao | undefined) =>
  selecao !== undefined && !selecao.conferindo && (!temAviso(selecao) || selecao.confirmado);

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const precoMetro = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const quando = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const mensagemDe = (falha: unknown, padrao: string) => {
  const dados = (falha as Falha).response?.data;
  const erros = dados?.errors;

  return erros ? Object.values(erros)[0][0] : (dados?.message ?? padrao);
};

/**
 * Passo 3 do fluxo: as propostas dos três fornecedores, rodada a rodada.
 *
 * O comprador escolhe o arquivo de cada fornecedor e só então apura — do mesmo
 * jeito que junta os três PDFs antes de montar a planilha de equalização. Os
 * arquivos vão um de cada vez ao servidor: cada leitura leva uns dez segundos
 * com o banco remoto, e juntar os três numa requisição só encostaria no limite
 * de um minuto.
 *
 * O vínculo manual mora no cartão, junto da linha que ficou sem preço — não
 * numa lista dos cem itens do PDF, que em quase tudo são a mesma linha em outra
 * cor.
 */
export default function PropostasPage() {
  const { cotacaoId } = useParams();
  const [parametros] = useSearchParams();
  const queryClient = useQueryClient();
  const podeEnviar = usePode('cotacao.importar_proposta');
  const inputRef = useRef<HTMLInputElement>(null);

  const [rodadaEscolhida, setRodadaEscolhida] = useState<number | null>(
    parametros.get('rodada') ? Number(parametros.get('rodada')) : null,
  );
  const [escolhendoPara, setEscolhendoPara] = useState<SituacaoFornecedor | null>(null);
  const [selecionados, setSelecionados] = useState<Record<number, Selecao>>({});
  const [processando, setProcessando] = useState<{ titulo: string; detalhe: string } | null>(null);
  const [erros, setErros] = useState<Record<number, string>>({});
  const [avisos, setAvisos] = useState<string[]>([]);
  const [escolhas, setEscolhas] = useState<Record<number, string>>({});

  const { data: cotacao } = useQuery({
    queryKey: ['cotacao', cotacaoId],
    queryFn: async () => (await api.get<{ data: Cotacao }>(`/cotacoes/${cotacaoId}`)).data.data,
  });

  const rodadaId = rodadaEscolhida ?? cotacao?.rodadas[cotacao.rodadas.length - 1]?.id ?? null;

  const { data: situacao, isLoading } = useQuery({
    queryKey: ['propostas', rodadaId],
    enabled: rodadaId !== null,
    queryFn: async () => (await api.get<{ data: Situacao }>(`/rodadas/${rodadaId}/propostas`)).data.data,
  });

  const atualizar = () => {
    queryClient.invalidateQueries({ queryKey: ['propostas', rodadaId] });
    queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
    queryClient.invalidateQueries({ queryKey: ['equalizacao'] });
  };

  const trocarDeRodada = (id: number) => {
    setRodadaEscolhida(id);
    setSelecionados({});
    setErros({});
    setAvisos([]);
  };

  const vincular = useMutation({
    mutationFn: async ({ itemId, linhaId }: { itemId: number; linhaId: number; fornecedorId: number }) =>
      (await api.post<{ message: string }>(`/proposta-itens/${itemId}/vincular`, { cotacao_item_id: linhaId })).data,
    onSuccess: (resposta, { linhaId }) => {
      setEscolhas((atual) => ({ ...atual, [linhaId]: '' }));
      setAvisos([resposta.message]);
      atualizar();
    },
    onError: (falha, { fornecedorId }) =>
      setErros((atual) => ({ ...atual, [fornecedorId]: mensagemDe(falha, 'Não foi possível vincular.') })),
  });

  // Arquivo solto fora de um cartão não pode abrir o PDF por cima do sistema
  // (é o que o navegador faz sozinho) e levar embora o que estava escolhido.
  const [arrastandoSobre, setArrastandoSobre] = useState<number | null>(null);
  useEffect(() => {
    const segurar = (evento: globalThis.DragEvent) => {
      if (evento.dataTransfer?.types.includes('Files')) evento.preventDefault();
    };
    const soltarFora = (evento: globalThis.DragEvent) => {
      segurar(evento);
      setArrastandoSobre(null);
    };
    window.addEventListener('dragover', segurar);
    window.addEventListener('drop', soltarFora);

    return () => {
      window.removeEventListener('dragover', segurar);
      window.removeEventListener('drop', soltarFora);
    };
  }, []);

  if (!cotacao) {
    return <Preloader telaCheia mensagem="Carregando cotação…" className="py-3xl" />;
  }

  // Depois de apurar, o próximo passo é a equalização: o botão dela ganha destaque.
  const faltaVerEqualizacao = cotacao.situacao.etapa === 'conferir_equalizacao';
  const primeiraRodada = situacao?.rodada.numero === 1;
  // Preço herdado também equaliza: na 2ª rodada, sem proposta nova, a tela
  // continua levando à equalização.
  const temPreco = situacao?.fornecedores.some((f) => f.linhas_com_preco + f.linhas_herdadas > 0) ?? false;
  const fila = situacao?.fornecedores.filter((f) => prontoParaApurar(selecionados[f.id])) ?? [];
  const aguardandoConfirmacao = Object.values(selecionados).filter((s) => !s.conferindo && temAviso(s) && !s.confirmado).length;

  /**
   * Guarda o arquivo no cartão e já confere no servidor. Se o comprador trocar
   * de arquivo antes da resposta chegar, a resposta velha é descartada.
   */
  const selecionar = async (fornecedor: SituacaoFornecedor, arquivo: File) => {
    setSelecionados((atual) => ({ ...atual, [fornecedor.id]: { arquivo, conferindo: true, conferencia: null, confirmado: false } }));
    setErros((atual) => ({ ...atual, [fornecedor.id]: '' }));
    setAvisos([]);

    const dados = new FormData();
    dados.append('fornecedor_id', String(fornecedor.id));
    dados.append('arquivo', arquivo);

    let conferencia: Conferencia | null = { fornecedor_do_arquivo: null, repeticoes: [] };

    try {
      conferencia = (await api.post<{ data: Conferencia }>(`/rodadas/${rodadaId}/propostas/conferir`, dados)).data.data;
    } catch (falha) {
      // Arquivo que nem passa na validação (extensão, fornecedor sem leitor de
      // PDF) sai do cartão com o motivo. Falha de rede não trava: o servidor
      // confere de novo ao apurar.
      if ((falha as Falha).response?.status === 422) {
        setSelecionados((atual) => {
          if (atual[fornecedor.id]?.arquivo !== arquivo) return atual;
          const resto = { ...atual };
          delete resto[fornecedor.id];
          return resto;
        });
        setErros((atual) => ({ ...atual, [fornecedor.id]: mensagemDe(falha, 'Arquivo recusado.') }));
        return;
      }
    }

    setSelecionados((atual) =>
      atual[fornecedor.id]?.arquivo !== arquivo
        ? atual
        : { ...atual, [fornecedor.id]: { arquivo, conferindo: false, conferencia, confirmado: false } },
    );
  };

  /**
   * Arrastar a proposta para o cartão do fornecedor faz o mesmo que escolher
   * pelo botão: o arquivo fica no cartão, é conferido na hora e entra na fila
   * de "Apurar cotações".
   */
  const podeSoltar = (evento: DragEvent) => podeEnviar && processando === null && evento.dataTransfer.types.includes('Files');

  const soltar = (fornecedor: SituacaoFornecedor, evento: DragEvent) => {
    evento.preventDefault();
    evento.stopPropagation();
    setArrastandoSobre(null);
    if (!podeSoltar(evento)) return;

    const arquivos = Array.from(evento.dataTransfer.files);
    if (arquivos.length !== 1) {
      setErros((atual) => ({ ...atual, [fornecedor.id]: 'Solte um arquivo por vez: cada cartão recebe a proposta de um fornecedor.' }));
      return;
    }

    if (!/.(pdf|xlsx?)$/i.test(arquivos[0].name)) {
      setErros((atual) => ({ ...atual, [fornecedor.id]: `"${arquivos[0].name}" não é PDF nem planilha (.xls ou .xlsx).` }));
      return;
    }

    void selecionar(fornecedor, arquivos[0]);
  };

  const tirar = (fornecedorId: number) =>
    setSelecionados((atual) => {
      const resto = { ...atual };
      delete resto[fornecedorId];
      return resto;
    });

  const confirmar = (fornecedorId: number) =>
    setSelecionados((atual) => ({ ...atual, [fornecedorId]: { ...atual[fornecedorId], confirmado: true } }));

  /** O arquivo estava no cartão errado: vai para o do fornecedor que ele cita, e é conferido de novo. */
  const levarPara = (deId: number, paraId: number) => {
    const selecao = selecionados[deId];
    const destino = situacao?.fornecedores.find((f) => f.id === paraId);
    if (!selecao || !destino) return;

    tirar(deId);
    void selecionar(destino, selecao.arquivo);
  };

  const apurar = async () => {
    const mensagens: string[] = [];
    const novosErros: Record<number, string> = {};
    const restantes = { ...selecionados };

    for (const [indice, fornecedor] of fila.entries()) {
      setProcessando({
        titulo: `Apurando ${indice + 1} de ${fila.length}: ${fornecedor.apelido}…`,
        detalhe: 'Lendo os itens de cada arquivo e casando com as linhas da cotação. Não feche esta janela.',
      });

      const dados = new FormData();
      dados.append('fornecedor_id', String(fornecedor.id));
      dados.append('arquivo', selecionados[fornecedor.id].arquivo);
      if (selecionados[fornecedor.id].confirmado) dados.append('confirmar', '1');

      try {
        const resposta = await api.post<{ message: string }>(`/rodadas/${rodadaId}/propostas`, dados);
        mensagens.push(resposta.data.message);
        delete restantes[fornecedor.id];
      } catch (falha) {
        // Um arquivo ruim não trava os outros: segue a fila e mostra o erro no cartão.
        novosErros[fornecedor.id] = mensagemDe(falha, 'Não foi possível ler a proposta.');
      }
    }

    setProcessando(null);
    setSelecionados(restantes);
    setErros(novosErros);
    setAvisos(mensagens);
    atualizar();
  };

  return (
    <div className="px-lg py-lg">
      {processando && <TelaProcessando titulo={processando.titulo} detalhe={processando.detalhe} />}

      {/* Com arquivo escolhido e ainda não apurado, o passo em andamento é o de apurar. */}
      <PassosDaCotacao
        cotacao={cotacao}
        atual={fila.length > 0 || parametros.get('etapa') === 'apurar' ? 'apurar' : 'propostas'}
        rodadaId={rodadaId}
      />

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.xls,.xlsx"
        className="sr-only"
        onChange={(evento) => {
          const arquivo = evento.target.files?.[0];
          evento.target.value = '';
          if (!arquivo || !escolhendoPara) return;

          void selecionar(escolhendoPara, arquivo);
        }}
      />

      <div className="mb-md flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <p className="text-[12px] font-bold uppercase tracking-wide text-muted">Propostas dos fornecedores</p>
          <h1 className="text-[18px] font-bold text-ink">
            <span className="mr-sm text-accent tabular-nums">Nº {cotacao.numero}</span>
            {cotacao.descricao}
          </h1>
          <p className="text-[13px] text-muted">
            <Link to={`/cotacoes/${cotacao.id}`} className="hover:text-accent hover:underline">
              Ver pedido de cotação
            </Link>
            {situacao ? ` · ${situacao.total_linhas} linhas` : ''}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-sm">
          {aguardandoConfirmacao > 0 && (
            <span className="flex items-center gap-xs text-[12px] font-bold text-warning">
              <AlertTriangle size={14} />
              {aguardandoConfirmacao === 1 ? '1 arquivo pede confirmação' : `${aguardandoConfirmacao} arquivos pedem confirmação`}
            </span>
          )}

          {temPreco && (
            <Link
              to={`/cotacoes/${cotacao.id}/equalizacao${rodadaId ? `?rodada=${rodadaId}` : ''}`}
              className={[
                'inline-flex items-center gap-xs rounded-md border px-md py-sm text-[13px] font-bold',
                faltaVerEqualizacao && fila.length === 0
                  ? 'border-accent bg-accent text-white hover:bg-accent/90'
                  : 'border-border bg-surface text-ink hover:border-accent',
              ].join(' ')}
            >
              <Scale size={15} />
              Ver equalização
            </Link>
          )}

          {podeEnviar && (
            <button
              type="button"
              onClick={apurar}
              disabled={fila.length === 0 || processando !== null}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Calculator size={15} />
              {fila.length === 0
                ? 'Apurar cotações'
                : `Apurar ${fila.length} ${fila.length === 1 ? 'proposta' : 'propostas'}`}
            </button>
          )}
        </div>
      </div>

      <div className="mb-md">
        <SeletorDeRodada rodadas={cotacao.rodadas} atual={rodadaId} aoEscolher={trocarDeRodada} />
      </div>

      {avisos.length > 0 && (
        <div className="mb-md flex items-start gap-sm rounded-md border border-success/30 bg-success/5 px-md py-sm text-[13px] text-ink">
          <CheckCircle2 size={15} className="mt-[2px] shrink-0 text-success" />
          <ul className="space-y-[2px]">
            {avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </div>
      )}

      {podeEnviar && fila.length === 0 && situacao && !situacao.fornecedores.some((f) => f.proposta) && (
        <p className="mb-md text-[12px] text-muted">
          Escolha o arquivo de cada fornecedor — ou arraste-o para o cartão dele — e clique em{' '}
          <b className="text-ink">Apurar cotações</b>.
        </p>
      )}

      {isLoading || !situacao ? (
        <Preloader telaCheia mensagem="Carregando propostas…" className="py-2xl" />
      ) : (
        <div className="grid items-start gap-md lg:grid-cols-3">
          {situacao.fornecedores.map((fornecedor) => {
            const total = situacao.total_linhas;
            const comPreco = fornecedor.linhas_com_preco;
            const herdadas = fornecedor.linhas_herdadas;
            const semPreco = fornecedor.linhas_sem_preco.length;
            const erro = erros[fornecedor.id];
            const selecionado = selecionados[fornecedor.id];
            const pedeConfirmacao = selecionado !== undefined && !selecionado.conferindo && temAviso(selecionado) && !selecionado.confirmado;

            return (
              <section
                key={fornecedor.id}
                onDragEnter={(evento) => {
                  if (!podeSoltar(evento)) return;
                  evento.preventDefault();
                  setArrastandoSobre(fornecedor.id);
                }}
                onDragOver={(evento) => {
                  if (!podeSoltar(evento)) return;
                  evento.preventDefault();
                  evento.dataTransfer.dropEffect = 'copy';
                  if (arrastandoSobre !== fornecedor.id) setArrastandoSobre(fornecedor.id);
                }}
                onDragLeave={(evento) => {
                  // Passar por cima de um elemento de dentro do cartão também dispara "leave".
                  if (!evento.currentTarget.contains(evento.relatedTarget as Node | null)) setArrastandoSobre(null);
                }}
                onDrop={(evento) => soltar(fornecedor, evento)}
                className={[
                  'relative overflow-hidden rounded-lg border bg-surface',
                  arrastandoSobre === fornecedor.id
                    ? 'border-accent ring-4 ring-accent/25'
                    : pedeConfirmacao
                    ? 'border-warning ring-2 ring-warning/15'
                    : selecionado
                      ? 'border-accent ring-2 ring-accent/15'
                      : 'border-border',
                ].join(' ')}
              >
                {arrastandoSobre === fornecedor.id && (
                  <div
                    className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-xs rounded-lg border-2 border-dashed border-accent bg-surface/90 text-center"
                    aria-hidden="true"
                  >
                    <FileUp size={28} className="text-accent" />
                    <p className="text-[15px] font-bold text-ink">Solte a proposta da {fornecedor.apelido}</p>
                    <p className="text-[12px] text-muted">
                      {selecionado || fornecedor.proposta ? 'Substitui o arquivo deste cartão · ' : ''}
                      {fornecedor.le_pdf ? 'PDF ou planilha preenchida' : 'Planilha preenchida (.xls ou .xlsx)'}
                    </p>
                  </div>
                )}

                <header className="flex items-center justify-between gap-sm bg-faixa px-md py-sm">
                  <div className="flex min-w-0 items-center gap-sm">
                    <LogoFornecedor logoUrl={fornecedor.logo_url} apelido={fornecedor.apelido} />
                    <div className="min-w-0">
                      <h2 className="text-[14px] font-bold text-white">{fornecedor.apelido}</h2>
                      <p className="truncate text-[11px] text-white/60">{fornecedor.nome}</p>
                    </div>
                  </div>
                  <Estado
                    selecao={selecionado}
                    temProposta={fornecedor.proposta !== null}
                    herdadas={herdadas}
                    semPreco={semPreco}
                  />
                </header>

                <div className="p-md">
                  <div className="mb-xs flex items-baseline justify-between text-[12px]">
                    <span className="text-muted">Linhas com preço</span>
                    <span className="font-bold tabular-nums text-ink">
                      {comPreco + herdadas} <span className="font-normal text-muted">de {total}</span>
                    </span>
                  </div>
                  <div
                    className="flex h-2 overflow-hidden rounded-full bg-surface-subtle"
                    role="img"
                    aria-label={`${comPreco} linhas com preço desta rodada, ${herdadas} herdadas, de ${total}`}
                  >
                    <span className="bg-success" style={{ width: `${(comPreco / total) * 100}%` }} />
                    <span className="bg-muted/40" style={{ width: `${(herdadas / total) * 100}%` }} />
                  </div>

                  {fornecedor.proposta ? (
                    <dl className="mt-md space-y-[2px] text-[12px]">
                      <div className="flex gap-xs">
                        <dt className="text-muted">Arquivo</dt>
                        <dd className="min-w-0 truncate text-ink" title={fornecedor.proposta.arquivo ?? ''}>
                          {fornecedor.proposta.arquivo}
                        </dd>
                      </div>
                      <div className="flex gap-xs">
                        <dt className="text-muted">Lidos</dt>
                        <dd className="text-ink">
                          <span className="tabular-nums">{fornecedor.proposta.itens_lidos}</span> itens
                          {fornecedor.proposta.importado_em &&
                            ` · ${quando.format(new Date(fornecedor.proposta.importado_em))}`}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="mt-md text-[12px] text-muted">
                      {herdadas > 0
                        ? `Sem proposta nova: mantém os ${herdadas} preços da rodada anterior.`
                        : 'Aguardando a proposta.'}
                    </p>
                  )}

                  {fornecedor.pedido_revisao && (
                    <p
                      className={[
                        'mt-sm flex items-start gap-xs rounded-md px-sm py-xs text-[12px]',
                        fornecedor.pedido_revisao.atendido ? 'bg-surface-subtle text-muted' : 'bg-warning/10 text-ink',
                      ].join(' ')}
                    >
                      <Send size={13} className={`mt-[2px] shrink-0 ${fornecedor.pedido_revisao.atendido ? '' : 'text-warning'}`} />
                      <span>
                        {fornecedor.pedido_revisao.atendido ? 'Pedido de nova proposta de ' : 'Nova proposta pedida em '}
                        <b>{quando.format(new Date(fornecedor.pedido_revisao.em))}</b>
                        {fornecedor.pedido_revisao.atendido
                          ? ' — atendido.'
                          : ` · alvo ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(fornecedor.pedido_revisao.pacote_alvo)}`}
                      </span>
                    </p>
                  )}

                  {selecionado && (
                    <div
                      className={[
                        'mt-md rounded-md border border-dashed px-sm py-sm',
                        pedeConfirmacao ? 'border-warning bg-warning/5' : 'border-accent bg-accent/5',
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-sm">
                        {selecionado.conferindo ? (
                          <Loader2 size={16} className="mt-[1px] shrink-0 animate-spin text-accent" />
                        ) : pedeConfirmacao ? (
                          <AlertTriangle size={16} className="mt-[1px] shrink-0 text-warning" />
                        ) : (
                          <FileText size={16} className="mt-[1px] shrink-0 text-accent" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[12px] font-bold text-ink" title={selecionado.arquivo.name}>
                            {selecionado.arquivo.name}
                          </p>
                          {!pedeConfirmacao && (
                            <p className="text-[11px] text-muted">
                              {selecionado.conferindo
                                ? 'Conferindo o arquivo…'
                                : fornecedor.proposta
                                  ? 'Substitui o arquivo atual ao apurar.'
                                  : 'Pronto para apurar.'}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => tirar(fornecedor.id)}
                          aria-label={`Tirar o arquivo da ${fornecedor.apelido}`}
                          className="shrink-0 rounded p-[2px] text-muted hover:bg-surface-subtle hover:text-ink"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      {pedeConfirmacao && selecionado.conferencia && (
                        <>
                          <ul className="mt-xs space-y-[2px] text-[12px] leading-snug text-ink">
                            {selecionado.conferencia.fornecedor_do_arquivo && (
                              <li>
                                Este arquivo parece ser da <b>{selecionado.conferencia.fornecedor_do_arquivo.apelido}</b>,
                                não da {fornecedor.apelido}.
                              </li>
                            )}
                            {selecionado.conferencia.repeticoes.slice(0, 2).map((repeticao) => (
                              <li key={`${repeticao.rodada}-${repeticao.fornecedor}`}>
                                {repeticao.mesmo_fornecedor && repeticao.nesta_rodada
                                  ? 'É o mesmo arquivo que já está apurado aqui.'
                                  : `É o mesmo arquivo já apurado para a ${repeticao.fornecedor} na ${repeticao.rodada.toLowerCase()}.`}
                              </li>
                            ))}
                          </ul>

                          <div className="mt-sm flex flex-wrap gap-xs">
                            {selecionado.conferencia.fornecedor_do_arquivo && (
                              <button
                                type="button"
                                onClick={() => levarPara(fornecedor.id, selecionado.conferencia!.fornecedor_do_arquivo!.id)}
                                className="rounded-md bg-faixa px-sm py-xs text-[12px] font-bold text-white"
                              >
                                Levar para a {selecionado.conferencia.fornecedor_do_arquivo.apelido}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => confirmar(fornecedor.id)}
                              className="rounded-md border border-border bg-surface px-sm py-xs text-[12px] font-bold text-ink hover:border-accent"
                            >
                              Usar mesmo assim
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {erro && (
                    <p className="mt-md flex items-start gap-xs rounded-md border border-danger/30 bg-danger/5 px-sm py-xs text-[12px] text-danger">
                      <AlertCircle size={14} className="mt-[1px] shrink-0" />
                      {erro}
                    </p>
                  )}

                  {podeEnviar && (
                    <button
                      type="button"
                      onClick={() => {
                        setEscolhendoPara(fornecedor);
                        inputRef.current?.click();
                      }}
                      className={[
                        'mt-md inline-flex w-full items-center justify-center gap-xs rounded-md px-md py-sm text-[13px] font-bold',
                        fornecedor.proposta || selecionado
                          ? 'border border-border bg-surface text-ink hover:border-accent'
                          : 'border border-accent bg-surface text-accent hover:bg-accent/5',
                      ].join(' ')}
                    >
                      {fornecedor.proposta && !selecionado ? <RefreshCw size={14} /> : <FileUp size={14} />}
                      {selecionado
                        ? 'Escolher outro arquivo'
                        : fornecedor.proposta
                          ? 'Trocar arquivo'
                          : primeiraRodada
                            ? 'Escolher arquivo'
                            : 'Escolher proposta nova'}
                    </button>
                  )}
                  {podeEnviar && !fornecedor.proposta && !selecionado && (
                    <p className="mt-xs text-center text-[11px] text-muted">
                      {fornecedor.le_pdf ? 'PDF ou planilha preenchida' : 'Planilha preenchida (.xls ou .xlsx)'} — ou arraste o
                      arquivo para este cartão
                    </p>
                  )}

                  {semPreco > 0 && (
                    <div className="mt-md border-t border-border pt-md">
                      <h3 className="mb-xs flex items-center gap-xs text-[12px] font-bold text-warning">
                        <AlertTriangle size={14} />
                        {semPreco === 1 ? '1 linha sem preço' : `${semPreco} linhas sem preço`}
                      </h3>
                      <p className="mb-sm text-[11px] leading-snug text-muted">
                        {fornecedor.proposta
                          ? 'Se o fornecedor cotou com outro nome, escolha o item. Se não cotou, deixe como está: a linha assume o menor preço e nunca vence.'
                          : 'Veio sem preço da rodada anterior. A linha assume o menor preço e nunca vence; para vincular, volte à rodada da proposta.'}
                      </p>

                      <ul className="space-y-sm">
                        {fornecedor.linhas_sem_preco.map((linha) => (
                          <li key={linha.id} className="rounded-md border border-border bg-dominant p-sm">
                            <p className="text-[12px] text-ink">
                              <span className="tabular-nums text-muted">{linha.ordem}.</span> {linha.produto}
                            </p>
                            <p className="mb-xs text-[11px] tabular-nums text-muted">
                              {inteiro.format(linha.quantidade_m)} m
                            </p>

                            {podeEnviar && fornecedor.itens_da_proposta.length > 0 && (
                              <div className="flex gap-xs">
                                <select
                                  value={escolhas[linha.id] ?? ''}
                                  onChange={(e) => setEscolhas((atual) => ({ ...atual, [linha.id]: e.target.value }))}
                                  aria-label={`Item da proposta da ${fornecedor.apelido} para ${linha.produto}`}
                                  className="min-w-0 flex-1 rounded-md border border-border bg-surface px-xs py-xs text-[12px] text-ink"
                                >
                                  <option value="">É algum item da proposta?</option>
                                  <optgroup label="Fora da cotação — os mais prováveis">
                                    {fornecedor.itens_da_proposta
                                      .filter((item) => item.fora_da_cotacao)
                                      .map((item) => (
                                        <OpcaoItem key={item.id} item={item} />
                                      ))}
                                  </optgroup>
                                  <optgroup label="Todos os itens da proposta">
                                    {fornecedor.itens_da_proposta
                                      .filter((item) => !item.fora_da_cotacao)
                                      .map((item) => (
                                        <OpcaoItem key={item.id} item={item} />
                                      ))}
                                  </optgroup>
                                </select>
                                <button
                                  type="button"
                                  disabled={!escolhas[linha.id] || vincular.isPending}
                                  onClick={() =>
                                    vincular.mutate({
                                      itemId: Number(escolhas[linha.id]),
                                      linhaId: linha.id,
                                      fornecedorId: fornecedor.id,
                                    })
                                  }
                                  className="inline-flex shrink-0 items-center gap-xs rounded-md bg-faixa px-sm text-[12px] font-bold text-white disabled:opacity-40"
                                >
                                  <Link2 size={13} />
                                  Vincular
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {fornecedor.itens_fora_da_cotacao.length > 0 && (
                    // Aberto e em destaque: produto que não foi pedido é assunto para
                    // falar com o fornecedor — substituto, outro nome ou engano.
                    <div className="mt-md overflow-hidden rounded-md border border-warning/40 bg-warning/5 text-[12px]">
                      <div className="px-sm pt-sm pb-xs">
                        <h3 className="flex items-center gap-xs font-bold text-warning">
                          <ListPlus size={15} aria-hidden="true" />
                          {fornecedor.itens_fora_da_cotacao.length === 1
                            ? '1 item cotado fora da lista'
                            : `${fornecedor.itens_fora_da_cotacao.length} itens cotados fora da lista`}
                        </h3>
                        <p className="mt-[2px] text-[11px] leading-snug text-ink-soft">
                          A {fornecedor.apelido} cotou produtos que não estão na cotação e que não entram na equalização. Confira
                          com o fornecedor: pode ser substituto, outro nome para um item da lista ou engano.
                          {semPreco > 0 && fornecedor.proposta ? ' Se for um item da lista, vincule na linha sem preço acima.' : ''}
                        </p>
                      </div>
                      <ul className="divide-y divide-warning/20 border-t border-warning/30 bg-surface">
                        {fornecedor.itens_fora_da_cotacao.map((item) => (
                          <li key={item.id} className="flex items-baseline justify-between gap-sm px-sm py-xs">
                            <span className="min-w-0 text-[12px] text-ink">{item.descricao}</span>
                            <span className="shrink-0 text-[12px] font-bold tabular-nums text-ink">
                              {item.preco_metro !== null ? `R$ ${precoMetro.format(item.preco_metro)}/m` : 'sem preço'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OpcaoItem({ item }: { item: ItemProposta }) {
  return (
    <option value={item.id}>
      {item.descricao}
      {item.preco_metro !== null ? ` — R$ ${precoMetro.format(item.preco_metro)}/m` : ''}
    </option>
  );
}

/** O selo do canto do cartão: o que o comprador precisa fazer com este fornecedor. */
function Estado({
  selecao,
  temProposta,
  herdadas,
  semPreco,
}: {
  selecao: Selecao | undefined;
  temProposta: boolean;
  herdadas: number;
  semPreco: number;
}) {
  const [rotulo, estilo] = selecao?.conferindo
    ? ['Conferindo', 'bg-white/15 text-white']
    : selecao && temAviso(selecao) && !selecao.confirmado
      ? ['Confirmar', 'bg-warning text-sobre-cor']
      : selecao
        ? ['Pronto para apurar', 'bg-accent text-white']
        : !temProposta
          ? herdadas > 0
            ? ['Herdada', 'bg-white/15 text-white']
            : ['Aguardando', 'bg-white/15 text-white/80']
          : semPreco > 0
            ? ['Revisar', 'bg-warning text-sobre-cor']
            : ['Completa', 'bg-success text-sobre-cor'];

  return <span className={`shrink-0 rounded-full px-sm py-[2px] text-[11px] font-bold ${estilo}`}>{rotulo}</span>;
}
